import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { Button, Link, Picture, Text } from 'components-guide';
import { NavLogoWeb, NavSearchDesktop } from '../';
import Router from 'router';
import Config from 'config';
import Info from 'config/info';

import './NavDesktop.scss';

export interface LinksProps {
    /**
     * id link
     */
    id?: string;
    /**
     * href
     */
    href?: string;
    /**
     * query params
     */
    params?: any;
    /**
     * text
     */
    text?: string;
    /**
     * Links of nav
     */
    children?: Array<LinksProps>;
}

export interface NavDesktopProps {
    /**
     * Links of nav
     */
    links?: Array<LinksProps>;
    /**
     * Invert color
     */
    invert?: boolean;
    /**
     * Translate prop
     */
    t?: any;
    /**
     * Class name styles
     */
    className?: string;
    /**
     * Style
     */
    style?: any;
    /**
     * Function called on click item
     */
    onClickItem?(item?: any): any;
    /**
     * Function called on click sign in button
     */
    onClickBecomeCustomer?(): any;
    /**
     * Function called on click sign up button
     */
    onClickSubscribe?(): any;
}

const SubMenuLink = ({ id, href, params, text, icon, onClick }: any) => (
    <li className="submenu-link">
        <Link iconSize={40} iconLeft={icon} className="flex row-left-center" onClick={() => onClick({ href, params })}>
            <Text tag="span" typo="small">
                {text}
            </Text>
        </Link>
    </li>
);

const SubMenuItem = ({ id, href, params, text, selected, children, onClick }: any) => (
    <li className={`submenu-item ${selected ? 'selected' : ''}`}>
        <div className="submenu-item__title">
            {href ? (
                <Link
                    className="flex row-center"
                    onClick={() => onClick({ href, params })}
                >
                    <Text tag="span">
                        {text}
                    </Text>
                </Link>
            ) : (
                <Text tag="span">
                    {text}
                </Text>
            )}
        </div>
        {(children || []).length ? (
            <ul>
                {children.map((item: any, index: any) => (
                    <SubMenuLink key={`submenu-link-${index}`} {...item} onClick={onClick} />
                ))}
            </ul>
        ) : null}
    </li>
);

const SubMenu = ({ id, items, className, onClick }: any) => {
    const classNames = classnames(['submenu', 'row-center', { [`submenu__${id}`]: id }, className]);
    return (
        <ul className={classNames}>
            {items.map((item: any, index: any) => (
                <SubMenuItem key={`submenu-item-${index}`} {...item} onClick={onClick} />
            ))}
        </ul>
    );
};

const MenuItem = ({ id, href, params, text, selected, children, invert, onClick }: any) => (
    <li key={id} className={`menu-item ${selected ? 'selected' : ''}`}>
        <Link className={`${invert ? `invert` : ''}`} onClick={() => onClick({ href, params })}>
            <Text tag="span" color={invert ? 'white' : 'black'} className="menu-item__title">
                {text}
            </Text>
        </Link>
        {(children || []).length ? <SubMenu id={id} items={children} onClick={onClick} /> : null}
    </li>
);

const Menu = ({ items, invert, onClick }: any) => (
    <ul className="menu">
        {items.map((item: any, index: any) => (
            <MenuItem key={`menu-${index}`} {...item} invert={invert} onClick={onClick} />
        ))}
    </ul>
);

const getLogo = ({ invert, isMobile }: any) => {
    const type = isMobile ? 'mobile' : 'desktop';
    return invert ? Info.logos[type].invert : Info.logos[type].default;
};

const NavDesktop: React.FC<NavDesktopProps> = ({
    links = [],
    invert = false,
    t,
    className,
    style,
    onClickItem = () => {},
    onClickBecomeCustomer = () => {},
    onClickSubscribe = () => {},
}) => {
    const classNames = classnames(['nav-desktop', className]);
    const [logo, setLogo] = useState('');
    const color = invert ? 'white' : undefined;

    useEffect(() => {
        setLogo(getLogo({ invert }));
    }, [invert]);

    const handleOnClickLogo = () => Router.push({ pathname: Info.urls.web, target: '_blank' });

    return (
        <nav className={classNames} style={style}>
            <div className="nav-desktop__cta">
                <div className="brand-desktop">
                    <a href="/blog" tabIndex={0}>
                        <Picture src={`${Config.themePath}${logo}`} alt="MyInvestor" className="logo" />
                    </a>
                </div>
                <NavSearchDesktop invert={invert} t={t} />
                <div className="flex row-center">
                    <Button color={color} onClick={onClickSubscribe}>
                        {t('navigation.subscribe')}
                    </Button>
                    <NavLogoWeb invert={invert} className="desktop" t={t} onClick={handleOnClickLogo} />
                </div>
            </div>

            {!!links.length && <Menu items={links} invert={invert} onClick={onClickItem} />}
        </nav>
    );
};

export default NavDesktop;
